import { Api, Model, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ButtonVariant, Image, ChangeEvent, Checkbox, DataSourceValidationEvent, ExcludableTextbox,
    LayoutProvidedTaskDecoratorProps, LookupModelSelectionEvent, Snackbar,
    Label
} from "@mcleod/components";
import { FuelPriceTable } from "@mcleod/general";
import { AutogenLayoutTransferToInvoicing } from "./autogen/AutogenLayoutTransferToInvoicing";

export class TransferToInvoicing extends AutogenLayoutTransferToInvoicing {
    private fuelPricesButton: Button;
    private fuelPriceWeeklyWarning: Label;
    private fuelPriceMonthlyWarning: Label;
    private successfulTransfers: String;

    public override get taskDecoratorProps(): Partial<LayoutProvidedTaskDecoratorProps> {

        const isComplete = (response) => {
            return response?.data?.[0]?.successful_transfers !== undefined;
        }
        const afterExecution = (response, data) => {
            if (!response || response?.length === 0) {
                Snackbar.showWarningSnackbar("No records to transfer");
            }
            const successes = data?.data?.[0]?.successful_transfers;
            if(successes !== undefined && successes > 1) {
                this.successfulTransfers = successes + " Invoices Transferred"
            }
            else if(successes !== undefined && successes === 1) {
                this.successfulTransfers = "1 Invoice Transferred"
            }
        }
        return {
            processLogDescription: "Transfer to Billing",
            headerAddlLeftTools: [this.createFuelPricesButton(), this.createFuelPriceWarnings(), this.fuelPriceMonthlyWarning],
            completionToastCondition: isComplete,
            /*
             * added ?? operator since sometimes this.successfulTransfers is undefined under certain 
             * conditions. This tooltip should never show without it being defined.
             */
            completionToastTitle: "Transfer Complete " + (this.successfulTransfers ?? ""),
            doAfterSearch: afterExecution,
            doAfterPost: afterExecution,
            supportsReadOnlyPreview: false,
            executeButtonCaption: "Transfer",
            confirmationDialogTitle: "Transfer Confirmation",
            confirmationDialogMessage: "Are you sure you want to transfer to invoicing?"
        };
    }

    override async onLoad() {
        this.textboxFilterType.selectedItem = this.textboxFilterType.items[0];
        if (this.textboxBillingMethod.items?.length > 0) {
            this.textboxBillingMethod.selectedItem = this.textboxBillingMethod.items[0];
        }

        //Keeping container stuff out of web for now.
        //this.textboxFilterType.visible = getDispatchControlBoolean("is_container_active", false);
 
        this.findComponentsByType(Checkbox).forEach(cb => cb.visible = false);

        this.excludabletextboxH.enabled = false;

        //Keeping dedicated billing stuff out of web for now.
        // const arControl = await Model.searchSingleRecord("lme/ar/arcontrol");
        // if (arControl?.get("is_dedicated_active", "N") === "Y") {
        //     this.textboxBillingMethod.visible = true;
        // }

        this.excludabletextboxOrderMove.switchRange.addChangeListener(() => {
            this.clearExcludeableRangeFields(this.excludabletextboxOrderMove);
        });

        const respControl = await Model.searchSingleRecord("lme/dispatch/agent-access-ctrl");
        if (respControl?.get("billing_filter", "N") === "Y") {
            this.textboxHierarchyLevel.visible = this.excludabletextboxH.visible = true;
        }
        this.updateDOEFuelPriceLabels();
    }

    private createFuelPricesButton(): Button {
        if (!this.fuelPricesButton) { 
            this.fuelPricesButton = new Button({
                variant: ButtonVariant.round,
                imageWidth: 24,
                imageHeight: 24,
                marginBottom: 0,
                imageName: ImageName.fuelPrice,
                color: "primary",
                tooltip: "DOE Fuel Prices",
                rowBreak: false
            });

            this.fuelPricesButton.addClickListener(() => {
                const slideout = FuelPriceTable.showInSlideoutDecorator();
                slideout.doAfterSlideOut = () => {
                    this.updateDOEFuelPriceLabels();
                };
            });
        }
        return this.fuelPricesButton;
    }

    private createFuelPriceWarnings(): Label {
        if (!this.fuelPriceWeeklyWarning) {
            this.fuelPriceWeeklyWarning = new Label({
                imageName: ImageName.circleError,
                color: "warning",
                tooltip: "Weekly DOE Fuel Prices Out of Date",
                visible: false,
                rowBreak: false,
                text: "Weekly Prices"
            });
        }
        if (!this.fuelPriceMonthlyWarning) {
            this.fuelPriceMonthlyWarning = new Label({
                imageName: ImageName.circleError,
                color: "warning",
                tooltip: "Monthly DOE Fuel Prices Out of Date",
                visible: false,
                rowBreak: false,
                text: "Monthly Prices"
            });
        }
        return this.fuelPriceWeeklyWarning;
    }

    private clearExcludeableRangeFields(tb: ExcludableTextbox) {
        this.mainDataSource.searchRow.set(tb.textboxRangeStart.field, null);
        this.mainDataSource.searchRow.set(tb.textboxRangeEnd.field, null);
    }
    
    /** This is an event handler for the onLookupModelSelection event of textboxHierarchyLevel. */
    textboxHierarchyLevelOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.excludabletextboxH.textbox.clear();
        this.excludabletextboxH.enabled = event.hasSelection();
    }

    /** This is an event handler for the beforeLookupModelSearch event of excludabletextboxH-textbox. */
    excludabletextboxHBeforeLookupModelSearch(event) {
        event.filter.hierarchy_level = this.textboxHierarchyLevel.lookupModelData[0].get("level_number");
    }

    /** This is an event handler for the onChange event of textboxFilterType. */
    textboxFilterTypeOnChange(event: ChangeEvent) {
        this.excludabletextboxOrderMove.visible = event.newValue.startsWith("Order");
        this.excludabletextboxMasterOrder.visible = event.newValue.startsWith("Master");
        this.excludabletextboxBookingNumber.visible = event.newValue.startsWith("Booking");
    }

    /** This is an event handler for the onValidate event of sourceTransferToInvoicingMultiple. */
    sourceTransferToInvoicingMultipleOnValidate(event: DataSourceValidationEvent) {
        const startValue = this.excludabletextboxOrderMove.textboxRangeStart?.getDataValue();
        const endValue = this.excludabletextboxOrderMove.textboxRangeEnd?.getDataValue();
        if (!StringUtil.isEmptyString(startValue) && !StringUtil.isEmptyString(endValue) && startValue > endValue) {
            event.validationResults.push({
                isValid: false,
                component: this.excludabletextboxOrderMove,
                caption: "Start value must be before end value."
            });
        }
    }

    updateDOEFuelPriceLabels() {
        Api.post("lme/ar/doe-fuel-date").then((result) => {
            const apiData = result.data[0];
            this.fuelPriceWeeklyWarning.visible = false;
            this.fuelPriceMonthlyWarning.visible = false;
            const weeklyDate = new Date(apiData.weekly_date);
            const monthlyDate = new Date(apiData.monthly_date);
            const today = new Date();
            const monthlyComparisonDate = new Date();
            const weeklyComparisonDate = new Date();
            monthlyComparisonDate.setDate(today.getDate() - 30);
            weeklyComparisonDate.setDate(today.getDate() - 7);

            // this.labelWeeklyDate.text = "Date of most recent fuel prices in weekly table: " + apiData.weekly_date;
            // this.labelMonthlyDate.text = "Date of most recent fuel prices in monthly table: " + apiData.monthly_date;
            if (weeklyDate <= weeklyComparisonDate) {
                this.fuelPriceWeeklyWarning.visible = true;
                this.fuelPriceWeeklyWarning.tooltip = "Date of most recent fuel prices in weekly table: " + apiData.weekly_date;
            }
            if (monthlyDate <= monthlyComparisonDate) {
                this.fuelPriceMonthlyWarning.visible = true;
                this.fuelPriceMonthlyWarning.tooltip = "Date of most recent fuel prices in monthly table: " + apiData.monthly_date;
            }

        });
    }
}
